import { Ban, ChevronRight, Clock, UserX } from "lucide-react";
import { BlocoData } from "@/modules/escala/components/BlocoData";
import { Pill } from "@/modules/escala/components/Pill";
import {
  formatarHora,
  marcadorMinhaResposta,
  rotuloDiaLongo,
  rotuloFuturo,
  type AbaEscala,
} from "@/modules/escala/format";
import type { EscalaComRelacoes } from "@/modules/escala/services/escalaService";

interface CardEscalaProps {
  escala: EscalaComRelacoes;
  aba: AbaEscala;
  onAbrir: () => void;
}

/**
 * Card da lista de escalas. Mostra UM marcador só (nunca a situação da escala
 * e a situação do participante juntas):
 * - Próximas (aba "agendadas"): a resposta da própria pessoa ("Aguardando sua resposta"...) ou, se ela
 *   não está na equipe, "Você não está na equipe" — só quando a leitura das participações
 *   deu certo (se falhou, nenhum selo: não dá para afirmar que está fora);
 * - Canceladas: só "Cancelada" + o motivo — a resposta fica para ver dentro da escala;
 * - Anteriores (aba "realizadas"): nenhum.
 */
export function CardEscala({ escala, aba, onAbrir }: CardEscalaProps) {
  const apagada = aba !== "agendadas";
  const minha = aba === "agendadas" && escala.minhaParticipacao ? marcadorMinhaResposta(escala.minhaParticipacao.resposta, escala.minhaParticipacao.origemResposta) : null;
  const foraDaEquipe = aba === "agendadas" && escala.participacoesLidas && !escala.minhaParticipacao;

  return (
    <button
      type="button"
      onClick={onAbrir}
      className={`relative flex w-full items-start gap-3.5 overflow-hidden rounded-2xl border border-border bg-surface p-3 text-left transition-colors hover:bg-surface-2/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        apagada ? "" : "before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-primary-hover"
      }`}
    >
      <BlocoData iso={escala.data_hora} estado={apagada ? "apagada" : "ativa"} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={`break-words text-[15px] font-bold leading-snug ${apagada ? "text-muted" : "text-text"}`}>
          {escala.nome}
        </span>
        <span className="flex flex-wrap items-center gap-x-1.5 text-[13px] text-muted">
          {/* O bloco de data ao lado é decorativo (aria-hidden): leitor de tela precisa da data por extenso. */}
          <span className="sr-only">{rotuloDiaLongo(escala.data_hora)},</span>
          <Clock size={14} aria-hidden className="shrink-0" />
          <span className="font-semibold text-text">{formatarHora(escala.data_hora)}</span>
          <span aria-hidden>·</span>
          <span>{escala.ministerioNome}</span>
          {aba === "agendadas" && (
            <>
              <span aria-hidden>·</span>
              <span>{rotuloFuturo(escala.data_hora)}</span>
            </>
          )}
        </span>
        {aba === "canceladas" && escala.motivo_cancelamento && (
          <span className="mt-0.5 line-clamp-2 break-words text-[12.5px] italic text-muted">
            {escala.motivo_cancelamento}
          </span>
        )}
        {aba === "canceladas" && (
          <span className="mt-1">
            <Pill tom="off" icone={Ban}>
              Cancelada
            </Pill>
          </span>
        )}
        {minha && (
          <span className="mt-1">
            <Pill tom={minha.tom} icone={minha.icone}>
              {minha.texto}
            </Pill>
          </span>
        )}
        {foraDaEquipe && (
          <span className="mt-1">
            <Pill tom="off" icone={UserX}>
              Você não está na equipe
            </Pill>
          </span>
        )}
      </span>
      <ChevronRight size={18} aria-hidden className="shrink-0 self-center text-muted" />
    </button>
  );
}
